<template>
  <div class="review-container">
    <router-link to="/marketing-tools">&larr; Kembali ke Daftar</router-link>
    
    <div v-if="loading">Memuat detail pinjaman...</div>
    <div v-if="error" class="error-message">{{ error }}</div>

    <div v-if="loan" class="loan-details">
      <h1>Review Pengajuan Pinjaman #{{ loan.id }}</h1>
      
      <div class="grid-container">
        <!-- Kolom 1: Detail & Checklist -->
        <div class="panel">
          <h3>Detail Pengajuan</h3>
          <p><strong>Peminjam:</strong> {{ loan.owner.full_name }}</p>
          <p><strong>Jumlah Diajukan:</strong> Rp {{ formatCurrency(loan.amount) }}</p>
          <p><strong>Tujuan:</strong> {{ loan.purpose }}</p>
          <p><strong>Tgl Diajukan:</strong> {{ formatDateTime(loan.created_at) }}</p>

          <h3 class="mt-4">Checklist Verifikasi Syarat</h3>
          <ul class="checklist">
             <li class="manual-check" :class="{ 'pass': manualChecks.isWNI === true, 'fail': manualChecks.isWNI === false }">
                <span>WNI Cakap Hukum:</span>
                <div class="check-actions">
                    <button @click="manualChecks.isWNI = true">Diterima</button>
                    <button @click="manualChecks.isWNI = false">Ditolak</button>
                </div>
            </li>
            <li :class="{ 'pass': checklist.isAgeValid }">
              Usia Peminjam ({{ checklist.age }} tahun): <strong>{{ checklist.isAgeValid ? '✓ Terpenuhi' : '✗ Tidak Sesuai' }}</strong>
            </li>
            <li class="manual-check" :class="{ 'pass': manualChecks.isEmployed === true, 'fail': manualChecks.isEmployed === false }">
            <span>Status Pegawai Tetap:</span>
                <div class="check-actions">
                    <button @click="manualChecks.isEmployed = true">Diterima</button>
                    <button @click="manualChecks.isEmployed = false">Ditolak</button>
                </div>
            </li>
          </ul>
        </div>
        
        <!-- Kolom 2: Simulasi Biaya -->
        <div class="panel">
          <h3>Simulasi Biaya & Angsuran</h3>
          <p><strong>Plafon Pembiayaan:</strong> Rp {{ formatCurrency(loan.amount) }}</p>
          <p><strong>Biaya Administrasi (0.5%):</strong> Rp {{ formatCurrency(loan.admin_fee) }}</p>
          <p><strong>Biaya Asuransi (Est. 1%):</strong> Rp {{ formatCurrency(loan.insurance_fee) }}</p>
          <p><strong>Biaya Materai:</strong> Rp {{ formatCurrency(loan.stamp_duty) }}</p>
          <hr>
          <p><strong>Total Pinjaman:</strong> Rp {{ formatCurrency(loan.total_loan) }}</p>
          <p><strong>Angsuran/Bulan (Est. 12x):</strong> Rp {{ formatCurrency(loan.estimated_monthly_installment) }}</p>
        </div>
      </div>

      <!-- Bagian Aksi -->
      <div class="actions-panel">
        <h3>Tindakan Verifikasi</h3>
        <p>Setelah me-review data di atas, pilih tindakan selanjutnya.</p>
        <div class="action-buttons">
          <button @click="handleVerification('Diverifikasi')" class="btn btn-primary" :disabled="!allChecksPassed">
            Verifikasi & Teruskan ke Supervisor
          </button>
          <button @click="handleVerification('Ditolak Awal')" class="btn btn-danger">
            Tolak Pengajuan
          </button>
          <p v-if="!allChecksPassed" class="error-message">Beberapa syarat tidak terpenuhi.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import { useToast } from 'vue-toastification';

const props = defineProps(['id']);
const router = useRouter();
const toast = useToast();

// State utama
const loan = ref(null);
const loading = ref(true);
const error = ref(null);

// State untuk verifikasi manual
const manualChecks = reactive({
  isWNI: null,
  isEmployed: null,
});

// =======================================================
// KEMBALIKAN 'checklist' COMPUTED PROPERTY
// =======================================================
const checklist = computed(() => {
  if (!loan.value?.owner?.profile) {
    return { age: 'N/A', isAgeValid: false };
  }
  
  const profile = loan.value.owner.profile;
  let age = 'N/A';
  let isAgeValid = false;
  
  if (profile.date_of_birth) {
    const birthDate = new Date(profile.date_of_birth);
    const today = new Date();
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    age = calculatedAge;
    isAgeValid = age >= 21 && age <= 52;
  }
  
  return { age, isAgeValid };
});
// =======================================================

// Cek apakah semua syarat terpenuhi
const allChecksPassed = computed(() => {
  // Gunakan 'checklist.value.isAgeValid'
  return checklist.value.isAgeValid && 
         manualChecks.isWNI === true && 
         manualChecks.isEmployed === true;
});

const handleVerification = async (newStatus) => {
  if (newStatus === 'Diverifikasi' && !allChecksPassed.value) {
    toast.error("Tidak bisa memverifikasi. Pastikan semua syarat terpenuhi.");
    return;
  }
  
  try {
    await api.verifyLoan(props.id, newStatus);
    toast.success(`Pinjaman #${props.id} berhasil diubah statusnya menjadi ${newStatus}.`);
    router.push('/marketing-tools');
  } catch (err) {
    toast.error("Gagal memperbarui status pinjaman.");
  }
};

// Hook saat komponen dimuat
onMounted(async () => {
  try {
    const response = await api.getLoanDetails(props.id);
    console.log("Data pinjaman diterima dari API:", response.data);
    console.log("Profil pemilik:", response.data.owner.profile);
    loan.value = response.data;
  } catch(err) {
    error.value = "Gagal memuat detail pinjaman.";
  } finally {
    loading.value = false;
  }
});

// Fungsi bantuan
const formatCurrency = (value) => parseFloat(value).toLocaleString('id-ID');
const formatDateTime = (value) => new Date(value).toLocaleString('id-ID');
</script>

<style scoped>
  /* Style untuk halaman review */
  .grid-container { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin: 2rem 0; }
  .panel { padding: 1.5rem; background: #f8f9fa; border-radius: 8px; }
  .checklist { list-style: none; padding: 0; margin-top: 1rem; }
  .checklist li { padding: 0.5rem; border-radius: 4px; margin-bottom: 0.5rem; }
  .checklist li.pass { background-color: #d4edda; color: #155724; } /* Hijau untuk yang lolos */
  .checklist li:not(.pass) { background-color: #f8d7da; color: #721c24; } /* Merah untuk yang gagal */
  .actions-panel { margin-top: 2rem; text-align: center; }
  .action-buttons { margin-top: 1rem; display: flex; justify-content: center; gap: 1rem; }
  .mt-4 { margin-top: 1.5rem; }
  .error-message { color: #dc3545; }
</style>